import {
  getGetApiCertificationProgramListQueryOptions,
  type CertificationProgramListItemDTO,
} from '@smartsash/api-client';
import { defineDataScreen, pagedQuery } from '@smartsash/data-screen';
import { Badge, Button, Icon } from '@smartsash/ui';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import styles from './Certifications.module.css';
import { CertificationsFilter } from './CertificationsFilter';
import { certificationColumnText, type CertificationColumnId } from './certificationsModel';
import { certificationsQueryParams, certificationsSearchSchema } from './certificationsModel';
import MobileCard from './MobileCard';
import RowActions from './RowActions';
import { useCreateCertificationDialog } from './useCreateCertificationDialog';

import { useDataScreenTranslate } from '@/hooks/useDataScreenTranslate';

/**
 * Column headers are rendered, not built: the definition lives at module scope and cannot call
 * `t`, but a component can.
 */
function ColumnHeader({ column }: { column: CertificationColumnId }) {
  const { t } = useTranslation();
  return <>{t(`certifications.columns.${column}`)}</>;
}

function StatusBadge({ row }: { row: CertificationProgramListItemDTO }) {
  const { t } = useTranslation();
  const active = row.status === 'active';
  return (
    <Badge variant="modern" size="sm" dot color={active ? 'success' : 'gray'}>
      {active ? t('certifications.status.active') : t('certifications.status.inactive')}
    </Badge>
  );
}

const columns: ColumnDef<CertificationProgramListItemDTO>[] = [
  {
    id: 'institute',
    header: () => <ColumnHeader column="institute" />,
    cell: ({ row }) => (
      <Badge variant="modern" size="sm" iconLeading={<Icon name="building-07" />}>
        {certificationColumnText.institute(row.original)}
      </Badge>
    ),
    meta: { width: '240px', sortable: true, value: certificationColumnText.institute },
  },
  {
    id: 'laboratory',
    header: () => <ColumnHeader column="laboratory" />,
    // DV-01/LT-07: the row links to its own programme. It pointed at the editor while no read view
    // existed; now that `/certifications/$id` does, the title opens it — which is what every other
    // entity's primary column does, and what makes "open" stop meaning "start editing".
    //
    // It stays `primary` even though Institution now leads the table: `primary` picks the mobile
    // card's title and the linked cell, which is still the programme's own laboratory.
    cell: ({ row }) => (
      <Link
        to="/certifications/$id"
        params={{ id: row.original.id ?? '' }}
        className={styles['lab-cell']}
      >
        {certificationColumnText.laboratory(row.original)}
      </Link>
    ),
    meta: { primary: true, sortable: true, value: certificationColumnText.laboratory },
  },
  {
    id: 'validityDays',
    header: () => <ColumnHeader column="validityDays" />,
    cell: ({ row }) => (
      <Badge variant="modern" size="sm" iconLeading={<Icon name="refresh-cw-05" />}>
        {certificationColumnText.validityDays(row.original)}
      </Badge>
    ),
    meta: { width: '120px', sortable: true, value: certificationColumnText.validityDays },
  },
  {
    id: 'videos',
    header: () => <ColumnHeader column="videos" />,
    // `videoCount`/`questionCount` are aggregated server-side; the list row carries no `items[]`
    // to count, and fetching each programme's detail to derive them would be one request per row.
    cell: ({ row }) => (
      <Badge variant="modern" size="sm" iconLeading={<Icon name="play-square" />}>
        {certificationColumnText.videos(row.original)}
      </Badge>
    ),
    meta: { width: '96px', sortable: true, value: certificationColumnText.videos },
  },
  {
    id: 'questions',
    header: () => <ColumnHeader column="questions" />,
    cell: ({ row }) => (
      <Badge variant="modern" size="sm" iconLeading={<Icon name="help-circle" />}>
        {certificationColumnText.questions(row.original)}
      </Badge>
    ),
    meta: { width: '96px', sortable: true, value: certificationColumnText.questions },
  },
  {
    id: 'status',
    header: () => <ColumnHeader column="status" />,
    cell: ({ row }) => <StatusBadge row={row.original} />,
    meta: { width: '120px', sortable: true, value: certificationColumnText.status },
  },
];

/**
 * The header's Add button and the dialog it opens (HS-1205).
 *
 * The form is a modal over the list rather than a route: creating a programme is five fields, and
 * the new row appears behind the dialog the moment the mutation invalidates the list. The
 * `/certifications/new` route still holds the unmigrated structure editor and is no longer reached
 * from here.
 */
function AddCertificationButton({ iconOnly = false }: { iconOnly?: boolean }) {
  const { t } = useTranslation();
  const openDialog = useCreateCertificationDialog();

  return iconOnly ? (
    <Button
      hierarchy="primary"
      size="sm"
      iconLeading={<Icon name="plus" />}
      iconOnly
      aria-label={t('certifications.addAriaLabel')}
      onClick={openDialog}
    />
  ) : (
    <Button hierarchy="primary" size="sm" iconLeading={<Icon name="plus" />} onClick={openDialog}>
      {t('certifications.add')}
    </Button>
  );
}

/**
 * The Certifications list on `@smartsash/data-screen` (HS-1162).
 *
 * This screen was the last mock list in the application, and it stayed mock for a structural
 * reason rather than an oversight: it renders a certification **programme** — one per laboratory,
 * owning an ordered set of videos and questions — and until `CertificationProgram` shipped, the
 * API modelled only the per-user certification **record**. Six of the six columns had nothing
 * behind them (`LT-07`).
 *
 * `apps/admin/CLAUDE.md` records this list as bespoke "because `GetApiCertificationParams` has no
 * paging at all". That was true of the old resource and is not true of this one: `/list` carries
 * `page`, `pageSize`, `search`, `sortBy`/`sortDir` and repeatable scope filters, which is exactly
 * the factory's contract.
 *
 * List-only by design, like every other migrated slice: `detail`, `form` and `permissions` stay
 * out while admin keeps its designed edit page, and per-route `requirePermission` remains unwired
 * (lock-out risk, HS-803). `searchScope`/`sortScope` are the factory's `'server'` default.
 */
export const certificationsScreen = defineDataScreen({
  entity: 'certificationProgram',
  searchSchema: certificationsSearchSchema,
  useTranslate: useDataScreenTranslate,
  list: {
    // The generated query options keep the generated key, which is what server-ops invalidates
    // after a delete — a bespoke key would leave the list stale.
    query: (search) =>
      pagedQuery<CertificationProgramListItemDTO>(
        getGetApiCertificationProgramListQueryOptions(certificationsQueryParams(search)),
      ),
    columns,
    getRowId: (row) => row.id ?? '',
    mobileCard: (row) => <MobileCard row={row} />,
    titleKey: 'certifications.title',
    emptyState: {
      filteredTitleKey: 'list.emptyFiltered.title',
      filteredDescriptionKey: 'list.emptyFiltered.description',
      titleKey: 'certifications.empty.title',
      descriptionKey: 'certifications.empty.description',
    },
    selectable: true,
    rowActions: [{ id: 'row-actions', render: (row) => <RowActions row={row} /> }],
    // HS-1396: the other five list screens have always set this; Certifications was the one that
    // offered its create button to every session. `CertificationProgram.Manage` is the catalogue's
    // only write key for programmes — there is no separate `.Create`.
    headerAction: {
      id: 'add',
      permission: 'certificationProgram.create',
      render: () => <AddCertificationButton />,
    },
    mobileHeaderAction: {
      id: 'add-mobile',
      permission: 'certificationProgram.create',
      render: () => <AddCertificationButton iconOnly />,
    },
    filterSlot: (search) => <CertificationsFilter search={search} />,
  },
});
